/**
 * useLastRunSummary.ts — lazily fetches a workflow's run history once (on
 * mount / id change) to derive its "Ran Xh ago · Completed" rail caption.
 * Deliberately not polled — the rail isn't the live-run surface, the
 * toolbar + RunSummaryPanel are.
 */

import { useEffect, useState } from "react";
import { fetchWorkflowRuns } from "../../services/workflow.service";

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const STATUS_LABEL: Record<string, string> = {
  completed: "Completed",
  failed: "Failed",
  stopped: "Stopped",
  running: "Running",
};

export function useLastRunSummary(workflowId: string) {
  const [caption, setCaption] = useState("Never run");

  useEffect(() => {
    let cancelled = false;
    fetchWorkflowRuns(workflowId)
      .then((res) => {
        if (cancelled) return;
        const latest = res?.data?.[0];
        if (!latest) {
          setCaption("Never run");
          return;
        }
        const label = STATUS_LABEL[latest.status] || latest.status;
        const when = latest.completedAt || latest.startedAt;
        setCaption(`Ran ${timeAgo(when)} · ${label}`);
      })
      .catch(() => {
        if (!cancelled) setCaption("Never run");
      });
    return () => {
      cancelled = true;
    };
  }, [workflowId]);

  return caption;
}
